
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { AlertTriangle, Eye, Phone, Shield, CheckCircle, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

const IdentifyRoofDamagePage = () => {
  return (
    <Layout>
      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              🕵️‍♂️ How to Identify Roof Damage Before It Costs You Thousands
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              Learn how to identify common signs of roof damage early. Protect your NC home with expert tips from Carolina Trophy Roofs.
            </p>
            <Button asChild className="bg-brand-carolina-blue hover:bg-brand-carolina-blue/90">
              <a href="tel:+12523712266">Call +1 (252) 371-2266 for Free Inspections</a>
            </Button>
          </div>

          {/* Introduction */}
          <Card className="mb-8">
            <CardHeader>
              <AlertTriangle className="h-8 w-8 text-brand-safety-orange mb-2" />
              <CardTitle>The Enemy You Can't See Will Empty Your Wallet</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-lg max-w-none">
              <p>
                Roof damage is a silent threat. It doesn't always announce itself with a dripping ceiling or fallen shingle. 
                Instead, it often hides—beneath layers of insulation, inside attic corners, or behind warped flashing—until 
                it becomes an expensive catastrophe.
              </p>
              <p>
                In North Carolina, where storms, humidity, and seasonal heat cycles stress even the best roofing systems, 
                early detection isn't just smart—it's <em>critical</em>.
              </p>
              <p>
                At <strong>Carolina Trophy Roofs</strong>, we've seen firsthand how small warning signs can balloon into 
                major repairs. This guide teaches you how to identify roof damage before it's too late—so you can stay 
                protected, dry, and stress-free.
              </p>
            </CardContent>
          </Card>

          {/* Why Early Detection Matters */}
          <Card className="mb-8">
            <CardHeader>
              <Shield className="h-8 w-8 text-brand-carolina-blue mb-2" />
              <CardTitle>Why Early Detection Matters</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>A $250 roof patch today could prevent a $12,000 replacement tomorrow.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Homeowner's insurance often requires prompt reporting of storm damage.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Water intrusion can lead to mold, wood rot, and electrical issues.</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-brand-carolina-blue/10 rounded-lg">
                <p className="font-semibold text-brand-carolina-blue">
                  👉 Want peace of mind? Schedule a free inspection at +1 (252) 371-2266.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Exterior Warning Signs */}
          <Card className="mb-8">
            <CardHeader>
              <Home className="h-8 w-8 text-brand-charcoal-gray mb-2" />
              <CardTitle>1. Exterior Warning Signs of Roof Damage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">🔹 Missing, Curled, or Cracked Shingles</h3>
                <p className="mb-2">Look for:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Shingles on the ground after a storm</li>
                  <li>Lifting edges (a sign of wind uplift)</li>
                  <li>Blistering, which indicates trapped moisture</li>
                </ul>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Did You Know?</strong> Granule loss shortens roof life by exposing the asphalt layer to UV damage.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">🔹 Damaged Flashing & Chimney Seals</h3>
                <p className="mb-2">Common signs:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Rust streaks or visible gaps</li>
                  <li>Water stains around chimney or skylights</li>
                  <li>Flashing that's bent, detached, or loose</li>
                </ul>
                <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Tip:</strong> Flashing failure is one of the top causes of chronic leaks and mold in attics.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">🔹 Sagging Roof Lines</h3>
                <p className="mb-2">
                  A dip or curve along the ridge of your roof is <strong>never normal</strong>. This often signals:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Rotted decking</li>
                  <li>Water-saturated insulation</li>
                  <li>Structural compromise</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Interior Clues */}
          <Card className="mb-8">
            <CardHeader>
              <Eye className="h-8 w-8 text-brand-trophy-gold mb-2" />
              <CardTitle>2. Interior Clues You Shouldn't Ignore</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">🔸 Attic Mold & Damp Insulation</h3>
                <p className="mb-2">Even without ceiling leaks, your attic may tell a different story. Check for:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Musty smells</li>
                  <li>Damp fiberglass insulation</li>
                  <li>Mold patches on plywood or rafters</li>
                </ul>
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Stat:</strong> Mold can begin forming within 24–48 hours of a moisture breach.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">🔸 Water Stains on Ceilings & Walls</h3>
                <p className="mb-2">Brownish spots, bubbling paint, or warped drywall can signal:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Slow leaks</li>
                  <li>Ice damming</li>
                  <li>Roof deck saturation</li>
                </ul>
                <div className="mt-3 p-3 bg-orange-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Important:</strong> Don't just patch the ceiling—<em>find the source.</em>
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">🔸 Spikes in Energy Bills</h3>
                <p className="mb-2">Sudden increases in heating/cooling costs could be due to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Compromised attic insulation</li>
                  <li>Ventilation failure caused by roof damage</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Storm-Specific Signs */}
          <Card className="mb-8">
            <CardHeader>
              <AlertTriangle className="h-8 w-8 text-brand-safety-orange mb-2" />
              <CardTitle>3. Storm-Specific Signs in North Carolina</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">Eastern NC homes frequently experience:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                <li>Hail damage (bruised shingles, dents)</li>
                <li>Wind uplift (shingle flapping or complete removal)</li>
                <li>Falling limbs (impact zones, punctures)</li>
              </ul>
              <div className="p-4 bg-brand-trophy-gold/10 rounded-lg">
                <p className="font-semibold">
                  📷 Pro Tip: After a storm, take smartphone pictures of your roof from the ground. 
                  Then call us to schedule a free post-storm inspection.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Inspection Checklist */}
          <Card className="mb-8">
            <CardHeader>
              <CheckCircle className="h-8 w-8 text-green-600 mb-2" />
              <CardTitle>4. Checklist: How to Inspect Your Roof Like a Pro (Safely!)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">From the Ground:</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Use binoculars to scan shingle lines</li>
                  <li>Look for discolored patches or uneven surfaces</li>
                  <li>Examine soffits and fascia boards for water damage</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">In the Attic:</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Use a flashlight to scan for mold, light beams, or moisture</li>
                  <li>Feel insulation for dampness</li>
                  <li>Smell for mildew or musty odors</li>
                </ul>
              </div>

              <div className="p-4 bg-red-100 border-l-4 border-red-500 rounded">
                <p className="font-semibold text-red-800">
                  🛑 NEVER climb your roof without proper gear. Let our pros handle it.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Why Choose Carolina Trophy Roofs */}
          <Card className="mb-8">
            <CardHeader>
              <Shield className="h-8 w-8 text-brand-carolina-blue mb-2" />
              <CardTitle>Why Homeowners Choose Carolina Trophy Roofs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Free professional inspections
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Drone and moisture scan technology
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Local expertise in storm & seasonal damage
                  </li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Fully insured
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    5-star service with excellent reviews
                  </li>
                </ul>
              </div>
              <div className="mt-6 text-center">
                <Button asChild size="lg" className="bg-brand-carolina-blue hover:bg-brand-carolina-blue/90">
                  <a href="tel:+12523712266">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Today: +1 (252) 371-2266
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">➤ What's the #1 sign of roof damage?</h3>
                <p>Missing or curled shingles are usually the first sign, especially after storms.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">➤ Will insurance cover roof damage?</h3>
                <p>If caused by a covered event (storm, hail, wind), most policies will—if filed promptly.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">➤ How often should I inspect my roof?</h3>
                <p>At least <strong>twice a year</strong> (spring and fall) and <strong>after any major storm</strong>.</p>
              </div>
            </CardContent>
          </Card>

          {/* Citations Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Sources & Further Reading</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p>
                  1. National Roofing Contractors Association - <a href="https://www.nrca.net/consumer-resources/be-red-zone-ready/homeowner-maintenance-and-replacement-information" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Homeowner Maintenance Guidelines</a>
                </p>
                <p>
                  2. Insurance Information Institute - <a href="https://www.iii.org/article/protecting-your-home-from-wind-damage" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Wind and Hail Damage Coverage</a>
                </p>
                <p>
                  3. EPA Guidelines on Mold - <a href="https://www.epa.gov/mold/mold-cleanup-your-home" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Mold Prevention and Cleanup</a>
                </p>
                <p>
                  4. Energy.gov - <a href="https://www.energy.gov/energysaver/weatherize/air-sealing-your-home/detecting-air-leaks" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Home Energy Efficiency Guidelines</a>
                </p>
                <p>
                  5. NC Climate Office - <a href="https://climate.ncsu.edu/climate_data" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">North Carolina Weather Patterns</a>
                </p>
              </div>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>Disclaimer:</strong> This information is for educational purposes. Always consult with roofing professionals for specific advice about your property.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Final CTA */}
          <Card className="bg-brand-carolina-blue text-white">
            <CardHeader>
              <CardTitle className="text-white">🎯 Final Word: Don't Wait for the Leak</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                Your roof never sleeps—and neither should your awareness.
              </p>
              <p className="mb-6">
                If you're seeing any of these signs—or even suspect damage—call <strong>Carolina Trophy Roofs</strong> today. 
                We'll perform a no-pressure inspection and give you expert feedback, fast.
              </p>
              <div className="text-center">
                <Button asChild size="lg" variant="secondary">
                  <a href="tel:+12523712266">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Now: +1 (252) 371-2266
                  </a>
                </Button>
                <p className="mt-4 text-sm">🛠️ Protect what protects your home.</p>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="mt-12 text-center">
            <Button variant="outline" asChild>
              <Link to="/resources">← Back to Resources</Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IdentifyRoofDamagePage;
